import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, EMPTY, throwError } from 'rxjs';
import { PrismHeaders } from '../../enums';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments';
import { AuthenticationService } from '../standard';
import { getAuth } from 'firebase/auth';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(public authService: AuthenticationService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers;
    const serviceToken = this.authService.sessionTokenRaw;

    // Skip authentication for static assets
    if (this.isStaticAsset(req.url)) {
        return next.handle(req);
    }

    if (!req.headers.has(PrismHeaders.SkipHeaders)) {
      if (!serviceToken) {
        return from(this.authService.sessionToken$)
          .pipe(
            switchMap(token => {
              if (!!token) {
                headers = req.headers
                  .set('Content-Type', 'application/json')
                  .set('Access-Control-Allow-Origin', `${environment.baseUrl}`)
                  .set(PrismHeaders.Authorization, `Bearer ${token}`);

                const reqClone = req.clone({
                  headers
                });

                return next.handle(reqClone).pipe(
                  catchError((error: HttpErrorResponse) => this.handleAuthError(error, reqClone, next))
                );
              } else {
                return EMPTY;
              }

            })
          );
      } else {
        headers = req.headers
          .set('Content-Type', 'application/json')
          .set(PrismHeaders.Authorization, `Bearer ${serviceToken}`);

        const reqClone = req.clone({
          headers
        });

        return next.handle(reqClone).pipe(
          catchError((error: HttpErrorResponse) => this.handleAuthError(error, reqClone, next))
        );
      }

    } else {
      headers = req.headers
        .delete(PrismHeaders.SkipHeaders);

      const reqClone = req.clone({
        headers
      });

      return next.handle(reqClone);
    }
  }

  private handleAuthError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If 401 error, try to refresh token and retry
    if (error.status === 401) {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        return from(currentUser.getIdToken(true)).pipe(
          switchMap(newToken => {
            // Update the token in the service
            this.authService['sessionTokenSource'].next(newToken);
            
            // Retry the request with the new token
            const newHeaders = request.headers.set(PrismHeaders.Authorization, `Bearer ${newToken}`);
            const newRequest = request.clone({ headers: newHeaders });
            
            return next.handle(newRequest);
          }),
          catchError(err => {
            // If refresh fails, logout and redirect
            this.authService.logout();
            return throwError(() => err);
          })
        );
      } else {
        // No current user, logout and redirect
        this.authService.logout();
      }
    }
    
    // If 403 error, user doesn't have access - logout and redirect
    if (error.status === 403) {
      console.error('Access denied - User does not have permission');
      this.authService.logout();
    }
    
    return throwError(() => error);
  }

  private isStaticAsset(url: string): boolean {
        // Check if the URL is for a static asset
        const staticAssetPatterns = [
            '/assets/',
            '.svg',
            '.png',
            '.jpg',
            '.jpeg',
            '.gif',
            '.webp',
            '.ico',
            '.css',
            '.js',
            '.woff',
            '.woff2',
            '.ttf',
            '.eot'
        ];

        return staticAssetPatterns.some(pattern => url.includes(pattern));
    }

}
